import { ChangeDetectorRef, Component, OnInit, OnDestroy, HostListener, ElementRef, ViewChild } from '@angular/core';
import { Router, NavigationStart, NavigationEnd, NavigationCancel, NavigationError } from '@angular/router';
import { ThemeService, Theme } from '../../core/services/theme.service';
import { AuthService } from '../../core/services/auth.service';
import { faUser, faBars, faUserDoctor, faBell } from '@fortawesome/free-solid-svg-icons';
import { DropdownOption } from '../../shared/components/dropdown/dropdown.component';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { DosespotService, DosespotNotificationRow } from '../../core/services/dosespot.service';
import { ErrorService } from '../../core/services/error.service';
import { AppStatusBarService } from '../../core/services/app-status-bar.service';
import { ProfileService } from '../../core/services/profile.service';

@Component({
  selector: 'app-main-layout',
  standalone: false,
  templateUrl: './main-layout.component.html',
  styleUrl: './main-layout.component.css'
})
export class MainLayoutComponent implements OnInit, OnDestroy {
  currentTheme: Theme = 'navy-medical-theme';
  isSidebarOpen = false;
  isProfileMenuOpen = false;
  /** Hides previous route and shows a neutral loader while lazy route chunks load. */
  isRouteLoading = false;
  /** Counts overlapping navigations so one NavigationEnd does not clear loading while another is in flight. */
  private activeNavigationCount = 0;
  private currentRoutePath = '';

  faUser = faUser;
  faBars = faBars;
  faUserDoctor = faUserDoctor;
  faBell = faBell;
  unreadDosespotCount = 0;
  dosespotNotifications: DosespotNotificationRow[] = [];
  isDosespotPanelOpen = false;
  dosespotNotificationsLoading = false;
  dosespotOpeningElabId: string | null = null;
  @ViewChild('profileMenuRef', { static: false }) profileMenuRef!: ElementRef;
  @ViewChild('dosespotBellRef', { static: false }) dosespotBellRef!: ElementRef;


  profileOptions: DropdownOption[] = [
    { label: 'My Profile', value: 'profile' },
    { label: 'Logout', value: 'logout' }
  ];
  private routerEventsSub?: Subscription;
  private dosespotUnreadSub?: Subscription;
  private dosespotListSub?: Subscription;

  constructor(
    private themeService: ThemeService,
    private router: Router,
    private authService: AuthService,
    private dosespotService: DosespotService,
    private errorService: ErrorService,
    private statusBar: AppStatusBarService,
    private profileService: ProfileService,
    private cdr: ChangeDetectorRef
  ) { }

  get userFullName(): string {
    const user = this.authService.getCurrentUser();
    if (!user) return '';
    const full = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
    return full || user.username || '';
  }

  get canAccessEMR(): boolean {
    return this.authService.canAccessEMR();
  }

  get userRoleLabel(): string {
    const user = this.authService.getCurrentUser();
    if (!user) return '';
    const isAdminRole =
      user.isAdministrator === true &&
      Array.isArray(user.roles) &&
      user.roles.some(
        (r) =>
          r.roleId === 8 &&
          typeof r.roleName === 'string' &&
          r.roleName.toUpperCase() === 'ADMINISTRATOR'
      );
    if (isAdminRole) return 'Administrator';
    if (Array.isArray(user.roles) && user.roles.length > 0 && user.roles[0].roleName) {
      return user.roles[0].roleName;
    }
    const t = user.userTypes;
    if (t?.doctor) return 'Doctor';
    if (t?.nurse) return 'Nurse';
    if (t?.frontDesk) return 'Front Desk';
    if (t?.biller) return 'Biller';
    return '';
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: Event): void {
    const target = event.target;
    if (this.isProfileMenuOpen) {
      try {
        const host = this.profileMenuRef?.nativeElement;
        if (host && !host.contains(target)) {
          this.isProfileMenuOpen = false;
        }
      } catch {
        this.isProfileMenuOpen = false;
      }
    }
    if (this.isDosespotPanelOpen) {
      try {
        const host = this.dosespotBellRef?.nativeElement;
        if (host && !host.contains(target)) {
          this.isDosespotPanelOpen = false;
        }
      } catch {
        this.isDosespotPanelOpen = false;
      }
    }
  }

  ngOnInit(): void {
    // Set default theme to navy-medical-theme
    this.themeService.setTheme('navy-medical-theme');
    this.currentTheme = this.themeService.getCurrentTheme();
    this.themeService.currentTheme$.subscribe(theme => {
      this.currentTheme = theme;
    });

    if (typeof window !== 'undefined') {
      this.updateSidebarState();

      window.addEventListener('resize', () => {
        this.updateSidebarState();
      });
    }

    this.currentRoutePath = this.router.url.split('?')[0] || '';

    this.routerEventsSub = this.router.events.subscribe((event) => {
      // Only navigation lifecycle events: lazy `RouteConfigLoad*` can fire during background preloading
      // or interleave with navigations and clear loading too early / leave it stuck.
      if (event instanceof NavigationStart) {
        const nextPath = (event.url || '').split('?')[0] || '';
        if (nextPath !== this.currentRoutePath) {
          this.activeNavigationCount++;
          this.isRouteLoading = this.activeNavigationCount > 0;
          this.cdr.markForCheck();
        }
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        if (event instanceof NavigationEnd) {
          this.currentRoutePath = (event.urlAfterRedirects || event.url || '').split('?')[0] || '';
        }
        this.activeNavigationCount = Math.max(0, this.activeNavigationCount - 1);
        this.isRouteLoading = this.activeNavigationCount > 0;
        this.cdr.markForCheck();
      }
    });

    if (this.canAccessEMR) {
      this.dosespotUnreadSub = this.dosespotService.unreadCount$.subscribe((n) => {
        this.unreadDosespotCount = n;
      });
      this.dosespotListSub = this.dosespotService.notifications$.subscribe((rows) => {
        this.dosespotNotifications = rows;
        this.dosespotNotificationsLoading = false;
      });
    }

    this.statusBar.initializeForAuthenticatedUser();
    this.profileService.getProfile().subscribe({
      next: (profile) => {
        this.statusBar.setUserIdleTimeLimit(profile?.userIdleTime);
        this.cdr.markForCheck();
      },
      error: () => {
        // Footer still works without profile idle limit.
      }
    });
  }

  ngOnDestroy(): void {
    this.routerEventsSub?.unsubscribe();
    this.dosespotUnreadSub?.unsubscribe();
    this.dosespotListSub?.unsubscribe();
    this.activeNavigationCount = 0;
    this.isRouteLoading = false;
  }

  private updateSidebarState(): void {
    if (window.innerWidth >= 1024) {
      this.isSidebarOpen = true;
    } else {
      this.isSidebarOpen = false;
    }
  }

  toggleSidebar(): void {
    this.isSidebarOpen = !this.isSidebarOpen;
  }

  toggleProfileMenu(): void {
    this.isProfileMenuOpen = !this.isProfileMenuOpen;
    if (this.isProfileMenuOpen) {
      this.isDosespotPanelOpen = false;
    }
  }

  toggleDosespotPanel(): void {
    if (!this.canAccessEMR) {
      return;
    }
    this.isDosespotPanelOpen = !this.isDosespotPanelOpen;
    if (this.isDosespotPanelOpen) {
      this.isProfileMenuOpen = false;
      this.loadDosespotNotifications();
    }
  }

  private loadDosespotNotifications(): void {
    this.dosespotNotificationsLoading = true;
    this.cdr.markForCheck();
    this.dosespotService
      .refreshNotificationsNow()
      .pipe(
        finalize(() => {
          this.dosespotNotificationsLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (rows) => {
          this.dosespotNotifications = rows;
          this.cdr.markForCheck();
        },
        error: () => {
          this.dosespotNotifications = [];
          this.cdr.markForCheck();
        }
      });
  }

  trackDosespotNotification(_index: number, row: DosespotNotificationRow): string | number {
    return row.id;
  }

  formatDosespotTime(iso: string | null): string {
    if (!iso) {
      return '';
    }
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
      return iso;
    }
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  }

  onDosespotNotificationClick(n: DosespotNotificationRow, event: Event): void {
    event.stopPropagation();
    if (!n.elabId || this.dosespotOpeningElabId) {
      if (!n.elabId) {
        this.errorService.showWarning('No eLab patient ID on this notification.');
      }
      return;
    }
    this.dosespotOpeningElabId = n.elabId;
    this.cdr.markForCheck();
    this.dosespotService
      .openNotificationInDosespot(n)
      .pipe(
        finalize(() => {
          this.dosespotOpeningElabId = null;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        error: (err: unknown) => {
          const msg =
            (err as { message?: string })?.message ??
            'Unable to open DoseSpot for this patient.';
          this.errorService.showError(msg);
        }
      });
  }

  isDosespotRowOpening(n: DosespotNotificationRow): boolean {
    return !!n.elabId && this.dosespotOpeningElabId === n.elabId;
  }

  onProfileAction(value: string): void {
    this.isProfileMenuOpen = false;
    if (value === 'profile') {
      this.router.navigate(['/user/profile']);
    } else if (value === 'logout') {
      this.authService.logout().subscribe({
        next: () => {
          // Navigation is handled in the logout service
        },
        error: (error) => {
          // Even if logout fails, navigate to login
          this.router.navigate(['/login']);
        }
      });
    }
  }

  closeSidebarOnMobile(): void {
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      this.isSidebarOpen = false;
    }
  }

  onSidebarOpenRequest(): void {
    this.isSidebarOpen = true;
  }
}
